// apps/web/src/components/onboarding/ScanShowcase.tsx
// The welcome hero, v6 — the journey stays (enter right → center → exit left,
// ~9s), but inside the phone it now tells CALOLY'S OWN story (owner): the
// camera view (full video) takes a photo with a real shutter press + flash,
// then the app's actual analyzing UI — dark canvas, the captured photo with
// the lime scan line, Cal thinking, "Cal is reading your plate…", shimmer
// rows — then the app's dark result sheet with the meal name, total calories
// counting up, and per-item rows, exactly the FoodSheet look. Reduced motion:
// the static result screen, centered.
import { useEffect, useRef, useState } from "react";
import scanFoodVideo from "../../assets/scan-food.mp4";
import scanFoodPoster from "../../assets/scan-food-poster.jpg";
import { Mascot } from "../Mascot";
import { useT } from "../../lib/i18n";

/** Shared backdrop for the welcome screens — plain white, like the reference. */
export const INTRO_BG: React.CSSProperties = { background: "#ffffff", colorScheme: "light" };

const PERIOD = 10000; // ~10s: journey + a real hold on the result
const KCAL = 600; // owner-corrected estimate for the footage (eel salad)
const TAU = Math.PI * 2;

const clamp01 = (v: number) => Math.min(1, Math.max(0, v));
const seg = (t: number, a: number, b: number) => clamp01((t - a) / (b - a));
const easeInOut = (p: number) => (p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2);

export function ScanShowcase() {
  const t = useT();
  const [p, setP] = useState(0.62); // static fallback: result screen
  const [reduced, setReduced] = useState(false);
  const raf = useRef(0);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setReduced(true);
      setP(0.62);
      return;
    }
    videoRef.current?.play().catch(() => { /* poster remains */ });
    const start = performance.now();
    const tick = (now: number) => {
      setP(((now - start) % PERIOD) / PERIOD);
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, []);

  // ── Journey ───────────────────────────────────────────────────────────────
  // Enter from the right; the EXIT is no longer a slide — after the result
  // the whole phone zooms toward the viewer, HOLDS (~2.5s) so the calories
  // can actually be read, then fades away smoothly and the loop restarts.
  const enterP = easeInOut(seg(p, 0.0, 0.11));
  const travelX = (1 - enterP) * 150;
  const travelRot = (1 - enterP) * 7;
  const zoomIn = reduced ? 0 : easeInOut(seg(p, 0.5, 0.62));    // phone comes close
  const fadeOut = reduced ? 0 : easeInOut(seg(p, 0.88, 0.98));  // smooth goodbye
  const phoneScale = 1 + 0.34 * zoomIn + 0.1 * fadeOut;
  const phoneOpacity = reduced ? 1 : 1 - fadeOut;
  // calm down the idle motion while zoomed so the number is steady
  const float = reduced ? 0 : Math.sin(TAU * p) * 4 * (1 - 0.75 * zoomIn);
  const sway = reduced ? 0 : Math.sin(TAU * p + 1.2) * 0.6 * (1 - 0.75 * zoomIn);

  // ── In-phone story: camera → shutter → analyzing → result ────────────────
  const SHUTTER_AT = 0.2;
  const shutterPress = seg(p, SHUTTER_AT - 0.015, SHUTTER_AT); // ring squeeze
  const flash = p > SHUTTER_AT && p < SHUTTER_AT + 0.03
    ? 1 - seg(p, SHUTTER_AT, SHUTTER_AT + 0.03) : 0;
  const analyzing = reduced ? 0 : easeInOut(seg(p, SHUTTER_AT + 0.01, SHUTTER_AT + 0.05)) * (1 - easeInOut(seg(p, 0.42, 0.47)));
  const scanY = seg(p, 0.24, 0.42);                   // lime sweep over the captured photo
  const result = reduced ? 1 : easeInOut(seg(p, 0.44, 0.52));
  const fill = reduced ? 1 : easeInOut(seg(p, 0.47, 0.62));    // done counting BEFORE the hold
  const kcal = Math.round(KCAL * fill);
  const showCamera = !reduced && p < SHUTTER_AT + 0.03;

  const ITEMS = [
    { key: "start.item_eel", kcal: 330 },
    { key: "start.item_greens", kcal: 90 },
    { key: "start.item_orange", kcal: 180 },
  ];

  return (
    <div className="relative w-full overflow-hidden" style={{ height: 560 }} aria-hidden="true">
      <div
        className="absolute left-1/2 top-1/2 w-[15.5rem] rounded-[2.7rem] p-[9px]"
        style={{
          background: "#0c0f14",
          boxShadow: "0 26px 60px rgba(16,23,17,0.28)",
          transform: `translate(-50%, -50%) translateX(${(reduced ? 0 : travelX).toFixed(2)}%) translateY(${float.toFixed(2)}px) rotate(${(sway + (reduced ? 0 : travelRot)).toFixed(3)}deg) scale(${phoneScale.toFixed(4)})`,
          transformOrigin: "50% 56%",
          opacity: phoneOpacity,
        }}
      >
        {/* side keys */}
        <span className="absolute -left-[3px] top-[5.5rem] h-9 w-[3px] rounded-l-full" style={{ background: "#0c0f14" }} />
        <span className="absolute -right-[3px] top-[7.5rem] h-16 w-[3px] rounded-r-full" style={{ background: "#0c0f14" }} />
        <div className="relative overflow-hidden rounded-[2.25rem]" style={{ background: "#0d1110", aspectRatio: "9 / 19.3" }}>

          {/* ── SCREEN 1: the camera (full video + shutter) ── */}
          <div className="absolute inset-0" style={{ opacity: showCamera ? 1 : 0, transition: "opacity 220ms ease" }}>
            {!reduced && (
              <video
                ref={videoRef}
                src={scanFoodVideo}
                poster={scanFoodPoster}
                autoPlay muted loop playsInline
                className="absolute inset-0 h-full w-full object-cover"
              />
            )}
            <div className="absolute inset-x-0 bottom-0 h-32" style={{ background: "linear-gradient(0deg, rgba(0,0,0,0.45), transparent)" }} />
            {/* shutter — presses itself right before the flash */}
            <div className="absolute inset-x-0 bottom-5 flex items-center justify-center">
              <span
                className="grid h-16 w-16 place-items-center rounded-full border-[3px] border-white/90"
                style={{ transform: `scale(${(1 - 0.12 * shutterPress).toFixed(3)})` }}
              >
                <span className="h-[3.4rem] w-[3.4rem] rounded-full bg-white" />
              </span>
            </div>
          </div>

          {/* the flash */}
          {flash > 0 && <div className="absolute inset-0 z-20 bg-white" style={{ opacity: flash.toFixed(2) }} />}

          {/* ── SCREEN 2: the app analyzing (FoodSheet look) ── */}
          <div className="absolute inset-0 px-3 pt-10" style={{ opacity: analyzing.toFixed(2) }}>
            <div className="relative overflow-hidden rounded-2xl border border-white/10">
              <img src={scanFoodPoster} alt="" className="w-full object-cover" style={{ maxHeight: 220 }} />
              {/* the app's lime scan line over the captured photo */}
              <div
                className="absolute inset-x-0 h-8"
                style={{
                  top: `calc(${(scanY * 100).toFixed(1)}% - 16px)`,
                  background: "linear-gradient(180deg, transparent, rgba(103,212,145,0.55), transparent)",
                  boxShadow: "0 0 16px 2px rgba(103,212,145,0.5)",
                }}
              />
              <div className="absolute bottom-2 inset-x-0 flex items-center justify-center gap-2">
                <Mascot pose="thinking" size={24} />
                <span className="text-[0.7rem] font-medium text-white drop-shadow">{t("scan.working")}</span>
              </div>
            </div>
            {/* shimmer rows, exactly like the sheet */}
            <div className="mt-3 space-y-2">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-9 rounded-xl bg-white/[0.07] animate-pulse" style={{ animationDelay: `${i * 150}ms` }} />
              ))}
            </div>
          </div>

          {/* ── SCREEN 3: the app's result sheet ── */}
          <div className="absolute inset-0 flex flex-col px-3 pt-9 text-white" style={{ opacity: result.toFixed(2) }}>
            <div className="overflow-hidden rounded-2xl border border-white/10">
              <img src={scanFoodPoster} alt="" className="w-full object-cover" style={{ maxHeight: 128 }} />
            </div>
            <div className="mt-3 rounded-2xl bg-white/[0.06] px-3.5 py-3">
              <div className="flex items-baseline justify-between">
                <span className="text-[0.72rem] font-medium uppercase tracking-wider text-white/55">{t("start.mealName")}</span>
                <span className="text-2xl font-semibold tabular-nums">
                  {kcal} <span className="text-[0.7rem] font-medium text-white/55">kcal</span>
                </span>
              </div>
              <div className="mt-0.5 text-[0.68rem] tabular-nums text-white/55">
                E {Math.round(35 * fill)}g · K {Math.round(50 * fill)}g · F {Math.round(30 * fill)}g
              </div>
            </div>
            <div className="mt-2 divide-y divide-white/10 rounded-2xl bg-white/[0.06]">
              {ITEMS.map((it, i) => (
                <div
                  key={it.key}
                  className="flex items-center justify-between px-3.5 py-2.5"
                  style={{ opacity: reduced ? 1 : clamp01((result - 0.15 * i - 0.1) * 3).toFixed(2) }}
                >
                  <span className="text-[0.72rem] text-white/85">{t(it.key)}</span>
                  <span className="text-[0.72rem] font-medium tabular-nums text-white/70">{Math.round(it.kcal * fill)} kcal</span>
                </div>
              ))}
            </div>
            <div className="mt-auto mb-4 rounded-full py-2.5 text-center text-[0.75rem] font-semibold" style={{ background: "#67d491", color: "#08130c" }}>
              {t("scan.add")}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
